import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { SUPPORTED_LANGUAGE_CODES, isRtlLanguage } from './languages';
import en from './locales/en.json';

const LANG_LOADERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  ja: () => import('./locales/ja.json'),
  ko: () => import('./locales/ko.json'),
  zh: () => import('./locales/zh.json'),
  de: () => import('./locales/de.json'),
  fr: () => import('./locales/fr.json'),
  es: () => import('./locales/es.json'),
  pt: () => import('./locales/pt.json'),
  hi: () => import('./locales/hi.json'),
  id: () => import('./locales/id.json'),
  ar: () => import('./locales/ar.json'),
  bn: () => import('./locales/bn.json'),
  ru: () => import('./locales/ru.json'),
  ur: () => import('./locales/ur.json'),
};

const loaded = new Set<string>(['en']);

async function ensureLanguageLoaded(lng: string) {
  if (loaded.has(lng)) return;
  const loader = LANG_LOADERS[lng];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
    loaded.add(lng);
  } catch {
    // Network or chunk failure → silently fall back to English.
  }
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    partialBundledLanguages: true,
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    // Locale bundles are lazy-loaded (see ensureLanguageLoaded), so resources
    // for a newly-selected language arrive AFTER `languageChanged` fires. Without
    // binding to the store's `added` event, components render once (still in the
    // fallback) and never re-render when the bundle lands — that's why the first
    // switch to a not-yet-loaded language leaves the sidebar labels untranslated.
    // Binding `added` re-renders every useTranslation consumer once it registers.
    react: { bindI18nStore: 'added' },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'keenez.lang',
    },
  });

const initialLng = i18n.language?.split('-')[0] || 'en';
if (initialLng !== 'en') {
  void ensureLanguageLoaded(initialLng);
}

i18n.on('languageChanged', (lng) => {
  const base = lng?.split('-')[0] || 'en';
  void ensureLanguageLoaded(base);
});

function applyHtmlLangDir(lng: string) {
  document.documentElement.lang = lng;
  document.documentElement.dir = isRtlLanguage(lng) ? 'rtl' : 'ltr';
}

applyHtmlLangDir(i18n.language || 'en');
i18n.on('languageChanged', applyHtmlLangDir);

void import('./geo').then(({ applyGeoOnFirstVisit }) => {
  void applyGeoOnFirstVisit();
});

export default i18n;
